import { useMemo } from "react";

import deepmerge from "deepmerge";
import { AxisLabelsFormatterContextObject, TooltipFormatterContextObject } from "highcharts";

import User from "@mapmycustomers/shared/types/User";

import { ActivityCount } from "@app/scene/home/component/Cards/util";
import SIMPLE_DATE_RANGE from "@app/scene/home/util/simple-date-range";
import { doesWeekStartFromMonday } from "@app/util/dates";
import { formatDate, userDisplayName } from "@app/util/formatters";

type TooltipFormatter = (this: TooltipFormatterContextObject) => string;
const tooltipFormatter = (dateFormatter: (date: Date | number) => string): TooltipFormatter =>
  function () {
    const name = this.series.name;
    return `${dateFormatter(this.key)}<br/><small>${name ? `${name}: ` : ""}${this.y}</small>`;
  };

export const getDateRangeTooltipFormatter = (rangeLabel: string) => {
  const dateFormatter = SIMPLE_DATE_RANGE.find(({ label }) => label === rangeLabel)?.dateFormatter;
  return dateFormatter ? tooltipFormatter(dateFormatter) : undefined;
};

const useActivitiesChartConfig = (
  defaultConfig: object,
  data: Record<User["id"], ActivityCount[]> | undefined,
  dateRange: string,
  selectedUsers: User[]
) =>
  useMemo(() => {
    const userIds = new Set(Object.keys(data ?? {}));
    const userList = selectedUsers.filter(({ id }) => userIds.has(`${id}`));

    return deepmerge.all([
      {},
      defaultConfig,
      {
        series: userList.map((user) => ({
          data: data?.[user.id].map(({ count, date }: ActivityCount) => [date.valueOf(), count]),
          name: userDisplayName(user),
          type: "line",
        })),
        time: {
          timezoneOffset: new Date().getTimezoneOffset(),
        },
        tooltip: {
          formatter: getDateRangeTooltipFormatter(dateRange),
        },
        xAxis: {
          labels: {
            formatter: function (this: AxisLabelsFormatterContextObject<number>): string {
              return formatDate(this.value, "P");
            },
          },
          startOfWeek: doesWeekStartFromMonday() ? 1 : 0,
          type: "datetime",
        },
      },
    ]);
  }, [data, dateRange, defaultConfig, selectedUsers]);

export default useActivitiesChartConfig;
